/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { OperationHandler } from 'src/core/services/api/createOperationCall';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { Region } from 'src/core/types/regionTypes';
import { isDevelopmentEnvironment, isStagingEnvironment } from 'src/core/utils/api/environment';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { DisposableLifetime } from 'src/core/utils/lifetime';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { GptJobSwagger } from 'src/customText/core/services/gpt/customTextGptSwagger';
import { GptEntityLabel } from 'src/customText/core/stores/CustomTextGptPredictedDocument';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { GptJobStore } from 'src/modules/gptPrediction/stores/GptJobStore';

export const getLabelId = (label: Partial<GptEntityLabel>) => `${label.entityId}-${label.startCharIndex}-${label.endCharIndex}`;

export const getGptJobOperationHandler = (
    lifetime: DisposableLifetime,
    gptJobsStoreHouse: ResourceStoreHouse<GptJobStore>
): OperationHandler<GptJobSwagger> => ({
    timeToDelay: OperationDelayStrategies.exponentialDelayStrategy,
    retryStrategy: res => !lifetime.isDisposed && res.data.status !== 'succeeded' && res.data.status !== 'failed',
    handleCall: data => {
        if (lifetime.isDisposed) {
            return;
        }

        let currentJobStore = gptJobsStoreHouse.findById(data.jobId);
        if (!currentJobStore) {
            currentJobStore = new GptJobStore(data.jobId, { status: 'inProgress' });
            gptJobsStoreHouse.add(currentJobStore);
        }

        const elapsedSeconds = Math.ceil(data.elapsedTime % 60);
        const elapsedMinutes = Math.floor((data.elapsedTime / 60) % 60);
        const elapsedHours = Math.floor((data.elapsedTime / (60 * 60)) % 24);

        currentJobStore.updateData({
            errors: data.status === 'failed' ? data.errors : null,
            status: data.status === 'running' ? 'inProgress' : data.status,
            elapsedTime: `${elapsedHours}:${elapsedMinutes}:${elapsedSeconds}`,
            createdDateTime: data.createdDateTime ? new Date(data.createdDateTime) : null,
            expirationDateTime: data.expirationDateTime ? new Date(data.expirationDateTime) : null,
            lastUpdatedDateTime: data.status === 'succeeded' ? new Date(data.lastUpdatedDateTime) : null
        });
    }
});

export const shouldRegionDisableGpt = (resourceRegion: Region) =>
    !isDevelopmentEnvironment() && !isStagingEnvironment() && !LuisConstants.CustomText.GptSupportedRegions.includes(resourceRegion);

export const shouldAuthTypeDisableGpt = (keyType: ServiceKey['keyType']) => !FeatureToggles.UseAADForGPT && keyType === 'aadToken';

export const isGptFeatureDisabled = (keyType: ServiceKey['keyType'], resourceRegion: Region) =>
    shouldRegionDisableGpt(resourceRegion) || shouldAuthTypeDisableGpt(keyType);
